import { Vector2 } from '@cgs/wge';
import { ComponentIndex } from '../../entities_engine/component_index';
import { EntitiesEngine } from '../../entities_engine/entities_engine';
import { Entity } from '../../entities_engine/entity';
import { BrakingCalculationInfo } from '../../entity_components/braking_calculation_info';
import { ComponentNames } from '../../entity_components/component_names';
import { SpeedInterpolation } from '../../entity_components/speed_interpolation';
import { EntityCacheHolder } from '../../game_components/entity_cache_holder';
import { IconEnumerator } from '../../icon_enumerator';
import { InternalReelsConfig } from '../../internal_reels_config';
import { BaseSystem } from '../../systems/base_system';
import { IntegrationHelper } from '../../utils/integration_helper';

const INTEGRATION_PRECISION = 0.0001;

export class FreeFallBeforeBrakingSystem extends BaseSystem {
  private readonly _indexesForFilter: ComponentIndex[];
  private readonly _brakingInfoIndex: ComponentIndex;
  private readonly _reelIndex: ComponentIndex;
  private readonly _enumerationIndex: ComponentIndex;
  private readonly _lineIndex: ComponentIndex;
  private readonly _reelsConfig: InternalReelsConfig;
  private readonly _iconEnumerator: IconEnumerator;
  private readonly _integrationHelper: IntegrationHelper;
  private readonly _integratedDest: number[];
  private readonly _integratedTime: number[];

  constructor(
    engine: EntitiesEngine,
    entityCacheHolder: EntityCacheHolder,
    reelsConfig: InternalReelsConfig,
    iconEnumerator: IconEnumerator,
    indexesForFilter: ComponentIndex[]
  ) {
    super(engine, entityCacheHolder);

    this._indexesForFilter = indexesForFilter;
    this._reelsConfig = reelsConfig;
    this._iconEnumerator = iconEnumerator;
    this._integrationHelper = new IntegrationHelper();
    this._integratedTime = new Array<number>(this._reelsConfig.reelCount);
    this._integratedDest = new Array<number>(this._reelsConfig.reelCount);

    for (let reel = 0; reel < reelsConfig.reelCount; ++reel) {
      const interpolator = new SpeedInterpolation()
        .withParameters(1.0, Vector2.One, Vector2.Zero, Vector2.lerp)
        .withTimeFunction(reelsConfig.getStopEasing(reel));
      this._integratedDest[reel] = this._integrationHelper.integrate(
        interpolator,
        INTEGRATION_PRECISION
      );
    }

    this._lineIndex = engine.getComponentIndex(ComponentNames.LineIndex);
    this._brakingInfoIndex = engine.getComponentIndex(ComponentNames.BrakingCalculationInfo);
    this._reelIndex = engine.getComponentIndex(ComponentNames.ReelIndex);
    this._enumerationIndex = engine.getComponentIndex(ComponentNames.EnumerationIndex);
  }

  public getIndexesForFilter(): ComponentIndex[] {
    return this._indexesForFilter;
  }

  public processEntity(entity: Entity): void {
    const reel = entity.get<number>(this._reelIndex);
    const offset = this._reelsConfig.reelsOffset[reel];
    const breakInfo = entity.get<BrakingCalculationInfo>(this._brakingInfoIndex);
    breakInfo.position.subtract(new Vector2(0.0, offset.y));
    const Y = breakInfo.position.y;
    const idx = entity.get<number>(this._lineIndex);
    const dst = idx * this._reelsConfig.symbolSize.y - Y;
    const destination = new Vector2(0.0, dst);
    const linearTime = destination.y / Math.abs(breakInfo.speed.y);
    this._integratedTime[reel] = linearTime / this._integratedDest[reel];

    const normalInterpolator = new SpeedInterpolation()
      .withParameters(1.0, Vector2.One, Vector2.Zero, Vector2.lerp)
      .withTimeFunction(this._reelsConfig.getStopEasing(reel));
    const dest = this._integrationHelper.integrate(
      normalInterpolator,
      1.0 / this._integratedTime[reel]
    );

    this._integratedTime[reel] = linearTime / dest;
    const interpolate = new SpeedInterpolation()
      .withParameters(
        this._integratedTime[reel],
        new Vector2(0.0, breakInfo.speed.y),
        Vector2.Zero.clone(),
        Vector2.lerp
      )
      .withTimeFunction(this._reelsConfig.getStopEasing(reel));

    const posInReel = entity.get<number>(this._lineIndex);
    const enumerationId = entity.get<number>(this._enumerationIndex);

    if (posInReel >= 0) {
      this._iconEnumerator.setMappedWinIndex(
        reel,
        posInReel,
        enumerationId + this._reelsConfig.lineCount
      );
    }

    entity.addComponent(ComponentNames.PositionInReel, posInReel);
    entity.addComponent(ComponentNames.CalculationReadyFlag, true);
    entity.addComponent(ComponentNames.RelocatedFlag, Math.sign(breakInfo.speed.y));
    entity.addComponent(ComponentNames.BrakingInterpolate, interpolate);
    entity.addComponent(
      ComponentNames.FinalPosition,
      new Vector2(breakInfo.position.x, posInReel * this._reelsConfig.symbolSize.y + offset.y)
    );
    entity.addComponent(
      ComponentNames.FinalEnumerationIndex,
      enumerationId + this._reelsConfig.lineCount
    );
    entity.removeComponent(ComponentNames.BrakingCalculationInfo);
    entity.removeComponent(ComponentNames.FreeFallingIcon);
  }
}
